import * as Crypto from 'expo-crypto';

import { firebaseAnalyticsAdapter } from '@/adapters/analytics';
import { appStateAdapter } from '@/adapters/appState';
import { createUserAwareAnalyticsAdapter } from '@/adapters/analytics/createUserAwareAnalyticsAdapter';
import { getCurrentAccountId } from '@/adapters/analytics/currentAccountId';
import { getCurrentUserId } from '@/adapters/analytics/currentUserId';
import { audioRecorder } from '@/adapters/audio/audioRecorder';
import { recordingLiveSurfaceAdapter } from '@/adapters/recordingLiveSurface/recordingLiveSurfaceAdapter';
import { voiceRecordingRepository } from '@/adapters/database/voiceRecordingRepository';
import { fileTransferAdapter } from '@/adapters/fileTransfer';
import { locationProvider } from '@/adapters/location/locationProvider';
import { expoLocalNotificationAdapter } from '@/adapters/localNotification';
import { sentryLogger } from '@/adapters/logger/sentryLogger';
import { networkAdapter } from '@/adapters/network';
import { createStorage } from '@/adapters/storage/createStorage';
import { fileStorage } from '@/adapters/storage/fileStorage';
import { i18n } from '@/i18n/instance';
import { MAX_RECORDING_DURATION_MS, START_RECORDING_TIMEOUT_MS } from '@/models/recordingDuration';
import {
  createVoiceRecordingStore,
  type VoiceRecordingAdapters,
} from '@/stores/createVoiceRecordingStore';
import { useGlobalAlertStore } from '@/stores/useGlobalAlertStore';
import { useAiTranscriptionConsentStore } from '@/stores/useAiTranscriptionConsentStore';
import { useSettingsStore } from '@/stores/useSettingsStore';

const canUpload = async (): Promise<boolean> => {
  const { allowMobileDataUpload } = useSettingsStore.getState();
  const networkState = await networkAdapter.getNetworkState();

  if (!networkState.isConnected) {
    return false;
  }

  if (allowMobileDataUpload) {
    return true;
  }

  return networkState.type === 'wifi';
};

const hasAnyNetwork = async (): Promise<boolean> => {
  const networkState = await networkAdapter.getNetworkState();

  return networkState.isConnected;
};

const hasAiTranscriptionConsent = async (): Promise<boolean> =>
  useAiTranscriptionConsentStore.getState().isAccepted;

const productionAdapters: VoiceRecordingAdapters = {
  audioRecorder,
  recordingLiveSurface: recordingLiveSurfaceAdapter,
  appState: appStateAdapter,
  locationProvider,
  repository: voiceRecordingRepository,
  fileStorage,
  generateId: () => Crypto.randomUUID(),
  logger: sentryLogger,
  analytics: createUserAwareAnalyticsAdapter(
    firebaseAnalyticsAdapter,
    () => getCurrentUserId(),
    () => getCurrentAccountId(),
  ),
  storage: createStorage(),
  fileTransferAdapter,
  networkAdapter,
  canUpload,
  hasAiTranscriptionConsent,
  hasAnyNetwork,
  notifyUploadPending: async (meetingId) => {
    await expoLocalNotificationAdapter.schedule({
      title: i18n.t('notifications.aiSummaryFailed.title'),
      body: i18n.t('notifications.aiSummaryFailed.body'),
      data: {
        resource_type: 'meeting',
        resource_id: meetingId,
      },
    });
  },
  notifyRecordingStopped: async (reason) => {
    await expoLocalNotificationAdapter.schedule({
      title: i18n.t(
        `recording.notifications.${reason === 'low_storage' ? 'lowStorage' : 'appKilled'}.title`,
      ),
      body: i18n.t(
        `recording.notifications.${reason === 'low_storage' ? 'lowStorage' : 'appKilled'}.body`,
      ),
      data: {
        resource_type: 'recording',
        reason,
      },
    });
  },
  config: {
    maxRecordingDurationMs: MAX_RECORDING_DURATION_MS,
    startTimeoutMs: START_RECORDING_TIMEOUT_MS,
    lowStorageThresholdBytes: 10 * 1024 * 1024,
    lowStorageWarningThresholdBytes: 50 * 1024 * 1024,
    lowStoragePreFlightThresholdBytes: 200 * 1024 * 1024,
    storageSampleIntervalMs: 10_000,
  },
};

const useVoiceRecordingStore = createVoiceRecordingStore(productionAdapters);

useVoiceRecordingStore.subscribe((state) => {
  if (!state.alert) {
    return;
  }
  useGlobalAlertStore.getState().showGlobalAlert(state.alert);
  useVoiceRecordingStore.setState({ alert: null });
});

// expo-router statically renders web routes in Node.js, where there is no
// window and no SQLite; opening the database there only spawns devtools noise.
if (typeof window !== 'undefined') {
  void voiceRecordingRepository
    .initialize()
    .then(() =>
      Promise.all([
        useVoiceRecordingStore.getState().loadPendingUploadMeetingIds(),
        useVoiceRecordingStore.getState().replayAppKilledMarker(),
      ]),
    )
    .catch((e) => {
      sentryLogger.captureException(e);
    });
}

const triggerRetry = () => {
  void useVoiceRecordingStore
    .getState()
    .retryPendingUploads()
    .then(() => {
      return useVoiceRecordingStore.getState().loadPendingUploadMeetingIds();
    })
    .catch((e) => {
      sentryLogger.captureException(e);
    });
};

networkAdapter.addEventListener((state) => {
  if (state.isConnected && state.isInternetReachable === true) {
    triggerRetry();
  }
});

appStateAdapter.addEventListener((status) => {
  if (status !== 'active') {
    return;
  }

  triggerRetry();
});

useSettingsStore.subscribe((current, previous) => {
  if (current.allowMobileDataUpload && !previous.allowMobileDataUpload) {
    triggerRetry();
  }
});

useAiTranscriptionConsentStore.subscribe((current, previous) => {
  if (current.isAccepted && !previous.isAccepted) {
    triggerRetry();
  }
});

export { useVoiceRecordingStore };
